import React from 'react';

import { createRoot } from 'react-dom/client';

import { ApolloProvider } from '@apollo/client';
import { loadClient as loadChrome } from '@justgiving/chrome';
import { init } from '@sentry/react';

import config from './config';
import { client } from './utils/graphqlClient';
import { initI18n } from './i18next';
import App from './roots/App/App';
import { isTestQueryString } from './utils';
import { handleTopLevelRedirects } from './utils/redirects';
import { getChromeHeaderVariant } from './utils/chrome';

async function initSentry() {
  const [{ BrowserTracing }, { Replay }] = await Promise.all([
    import(/* webpackChunkName: "sentry-tracing" */ '@sentry/tracing'),
    import(/* webpackChunkName: "sentry-replay" */ '@sentry/replay'),
  ]);

  init({
    dsn: config.sentryDsn,
    integrations: [
      new BrowserTracing(),
      new Replay({
        sessionSampleRate: 0.1,
        errorSampleRate: 1,
        maskAllText: false,
      }),
    ],
    tracesSampleRate: 0.1,
    environment: config.env,
    allowUrls: [/justgiving\.com/, /jg-cdn\.com/],
    ignoreErrors: [
      /not found/i,
      /must be logged in/i,
      /CookieConsent/,
      /Error: refreshAccessToken response not ok: 422, Unprocessable Entity/,
    ],
  });
}

async function prepare() {
  if (!isTestQueryString('msw', 'true')) return;

  const { worker } = await import(/* webpackChunkName: "msw" */ './mocks/browser');

  return worker.start({
    serviceWorker: {
      url: `${config.pathPrefix}mockServiceWorker.js`,
      options: {
        scope: config.pathPrefix.slice(0, -1),
      },
    },
  });
}

const redirectUrl = handleTopLevelRedirects(window.location.pathname);

if (redirectUrl) {
  window.location.href = redirectUrl;
} else {
  loadChrome({
    headerVariant: getChromeHeaderVariant(new URL(window.location.href)),
    footerVariant: 'slim',
  });

  initSentry();

  prepare()
    .then(initI18n)
    .then(() => {
      createRoot(document.getElementById('app')!).render(
        <React.StrictMode>
          <ApolloProvider client={client}>
            <App />
          </ApolloProvider>
        </React.StrictMode>
      );
    });
}
